import { useRef } from 'react';
import { AppleLogin, FacebookLogin, GoogleLogin } from '@belong/ui';
import type {
  AppleResponse,
  GoogleAuthResponse,
  GoogleAuthUnrelatedErrorResponse,
  GoogleAuthErrorResponse,
  GoogleLoginError,
  ReactFacebookFailureResponse,
  ReactFacebookLoginInfo,
} from '@belong/ui';
import { ExternalLoginModel } from 'src/api/models';
import {
  FACEBOOK_APP_ID,
  GOOGLE_OAUTH_CLIENT_ID,
  LOGIN_PROVIDER,
  REDIRECT_URI,
} from 'src/features/auth/constants';
import { useExternalLogin } from 'src/features/auth/queries';
import type { LoginProviders } from 'src/features/auth/types';
import { isFacebookFailureResponse } from 'src/features/auth/utils';
import { useMutateUser } from 'src/features/users/useUser';

type Props = Partial<{
  onError: (error: unknown) => void;
  onFinish:
    | ((hasError: boolean) => Promise<void>)
    | ((hasError: boolean) => void);
  onStart: () => void;
  onSuccess: (() => Promise<void>) | (() => void);
  onSuccessWithoutPhoneOrEmail:
    | ((provider: LoginProviders) => Promise<void>)
    | ((provider: LoginProviders) => void);
}>;

export function ExternalLogin({
  onError = () => {},
  onFinish = async () => {},
  onStart = () => {},
  onSuccess = async () => {},
  onSuccessWithoutPhoneOrEmail = async () => {},
}: Props) {
  const hasError = useRef(false);
  const { externalLogin } = useExternalLogin();
  const { mutateAsync: fetchUser } = useMutateUser();

  async function finishExternalLogin(
    loginRequest: ExternalLoginModel,
    provider: LoginProviders,
  ) {
    try {
      onStart();

      await externalLogin(loginRequest);

      const user = await fetchUser();

      if (!user.phone || !user.email) {
        hasError.current = true;

        await onSuccessWithoutPhoneOrEmail(provider);

        return;
      }

      await onSuccess();

      hasError.current = false;
    } catch (error) {
      onError(error);

      hasError.current = true;
    } finally {
      await onFinish(hasError.current);
    }
  }

  async function handleGoogleSuccess(response: GoogleAuthResponse) {
    try {
      const { code } = response as GoogleAuthResponse;

      const googleLoginRequest = {
        loginProvider: LOGIN_PROVIDER.Google,
        accessToken: code,
        loginExperiment: 'GoogleAuthFlow',
      };

      await finishExternalLogin(googleLoginRequest, LOGIN_PROVIDER.Google);
    } catch (error) {
      onError(error);
    }
  }

  function handleGoogleFailure(
    error:
      | GoogleLoginError
      | GoogleAuthUnrelatedErrorResponse
      | GoogleAuthErrorResponse,
  ) {
    onError(error);
  }

  async function handleFacebookLogin(
    facebookResponse: ReactFacebookFailureResponse | ReactFacebookLoginInfo,
  ) {
    if (isFacebookFailureResponse(facebookResponse)) {
      return;
    }

    const facebookLoginRequest: ExternalLoginModel = {
      loginProvider: LOGIN_PROVIDER.Facebook,
      accessToken: facebookResponse.accessToken,
    };

    await finishExternalLogin(facebookLoginRequest, LOGIN_PROVIDER.Facebook);
  }

  async function handleAppleLogin(response: AppleResponse) {
    if (response.authorization) {
      const appleLoginRequest: ExternalLoginModel = {
        loginProvider: LOGIN_PROVIDER.Apple,
        accessToken: response.authorization.id_token,
        ...(response.user && {
          email: response.user.email,
          firstName: response.user.name.firstName,
          lastName: response.user.name.lastName,
        }),
      };

      await finishExternalLogin(appleLoginRequest, LOGIN_PROVIDER.Apple);
    } else {
      console.error(response);

      onError(response.error);
    }
  }

  return (
    <div className="flex w-full flex-col">
      <div className="mb-sm">
        <GoogleLogin
          clientId={GOOGLE_OAUTH_CLIENT_ID}
          onFailure={handleGoogleFailure}
          onSuccess={handleGoogleSuccess}
        />
      </div>
      <div className="mb-sm">
        <FacebookLogin
          appId={FACEBOOK_APP_ID}
          autoLoad={false}
          callback={handleFacebookLogin}
          fields="name,email,picture"
          variant="secondary"
        />
      </div>
      <AppleLogin callback={handleAppleLogin} redirectURI={REDIRECT_URI} />
    </div>
  );
}
